import { Task } from 'src/@types/graphql'
import {
  DataGridPro,
  getGridStringOperators,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterModel,
  GridPaginationModel,
  GridRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { S } from 'src/shared/styled/S'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ReactElement, useMemo, useState } from 'react'
import { ALL_JOURNAL_TASK_TYPES, TASK } from 'src/shared/constants/task-constants'
import { PROCESS, PROJECT, PROJECT_TYPE } from 'src/shared/constants/constants'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { GridToolbar } from 'src/shared/grid/GridToolbar'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import styled from 'styled-components'
import { TEXT_OVERFLOW_MIXIN } from 'src/shared/constants/styling-constants'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { Option } from 'src/shared/form/control'
import { AutoCompleteFilterInput } from 'src/shared/form/grid/filter/AutoCompleteFilterInput'
import { commonlyUsedStringOperators, getGridStringsOperatorsIgnoreAccents } from 'src/shared/grid/DataGridFilters'

interface Props {
  journalTasks: Task[]
  loading: boolean
}

const DataGridProStyled = styled(DataGridPro)`
  & .MuiDataGrid-row {
    &:hover {
      cursor: pointer;
    }
  }
  margin-top: ${({ theme }) => theme.spacing(5)};
`

const DataGridTypographyStyled = styled(S.DataGrid.Typography)`
  ${TEXT_OVERFLOW_MIXIN};
`

const getTypeFilterOperators = (options: Option[], label: string, placeholder: string) =>
  getGridStringOperators()
    .filter((operator) => operator.value === 'equals')
    .map((operator) => ({
      ...operator,
      InputComponent: (gridFilterProps: GridFilterInputValueProps) => (
        <AutoCompleteFilterInput {...gridFilterProps} options={options} label={label} placeholder={placeholder} />
      ),
    }))

const getEntityIdFilterOperators = () => getGridStringOperators().filter((operator) => operator.value === 'equals')

const getEntityShortTitleFilterOperators = () =>
  getGridStringsOperatorsIgnoreAccents().filter((operator) =>
    commonlyUsedStringOperators.some((item) => item.includes(operator.value)),
  )

const getResponsibleFilterOperators = (options: Option[], label: string, placeholder: string) =>
  getGridStringOperators()
    .filter((operator) => operator.value === 'equals')
    .map((operator) => ({
      ...operator,
      InputComponent: (gridFilterProps: GridFilterInputValueProps) => (
        <AutoCompleteFilterInput {...gridFilterProps} options={options} label={label} placeholder={placeholder} />
      ),
    }))

export const JournalTaskGrid = ({ journalTasks, loading }: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const apiRef = useGridApiRef()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  })
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 })

  const rows = useMemo(() => {
    return journalTasks.map((item) => {
      return {
        ...item,
        responsible:
          item.responsiblePerson ?? getMessage(`label.task.responsible.group.${item.responsibleGroupMessageKey}`),
      }
    })
  }, [journalTasks, getMessage])

  const typeOptions = useMemo(
    () =>
      ALL_JOURNAL_TASK_TYPES.map((task) => {
        return { label: getMessage(`label.task.${task}`), value: task }
      }),
    [getMessage],
  )

  const responsibleOptions = useMemo(
    () =>
      journalTasks
        .map((task) => {
          return {
            responsible:
              task.responsiblePerson || getMessage(`label.task.responsible.group.${task.responsibleGroupMessageKey}`),
          }
        })
        .filter((task, index, self) => self.findIndex((o: any) => o.responsible === self[index].responsible) === index)
        .map((task) => {
          return { label: task.responsible, value: task.responsible }
        }) as Option[],
    [journalTasks, getMessage],
  )

  const columns = useMemo(() => {
    return [
      {
        field: 'taskType',
        headerName: getMessage('label.project.type'),
        flex: 1.5,
        filterOperators: getTypeFilterOperators(
          typeOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        renderCell: (props) => {
          return (
            <S.DataGrid.CenterDataGridCell>
              <S.DataGrid.TwoLineCell>
                <DataGridTypographyStyled variant="subtitle2">
                  {getMessage(`label.task.${props.value}`)}
                </DataGridTypographyStyled>
                {props.row?.milestoneType && (
                  <S.DataGrid.Typography variant="caption" color="textSecondary">
                    {getMessage(`label.milestone.type.${props.row.milestoneType}`)}
                  </S.DataGrid.Typography>
                )}
              </S.DataGrid.TwoLineCell>
            </S.DataGrid.CenterDataGridCell>
          )
        },
      },
      {
        field: 'entityId',
        headerName: getMessage('label.task.grid.id'),
        renderCell: ({ row }) => {
          if (row.process === PROJECT.KAP) {
            return `${getMessage('label.program.id.character')}${row.entityId}`
          } else if (row.process === PROJECT.PF_PGV || row.process === PROJECT.PF_KAP) {
            const process = row.process.toLowerCase()
            return `${getMessage(`label.project.id.character.${process}`)}${row.entityId}`
          } else if (row.process === PROCESS.FACTSHEET) {
            return `${getMessage(`label.factsheet.id.character`)}${row.entityId}`
          }
        },
        flex: 0.5,
        filterOperators: getEntityIdFilterOperators(),
      },
      {
        field: 'entityShortTitle',
        headerName: `${getMessage('label.project')} / ${getMessage('label.canton')}`,
        flex: 1.5,
        filterOperators: getEntityShortTitleFilterOperators(),
      },
      {
        field: 'responsible',
        headerName: getMessage('label.responsible'),
        flex: 1.2,
        filterOperators: getResponsibleFilterOperators(
          responsibleOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        renderCell: (props) => {
          return (
            <S.DataGrid.CenterDataGridCell>
              <S.Avatar $randomColorKey={props.value!.toString()}>{props.value?.toString()?.charAt(0)}</S.Avatar>{' '}
              <S.Span.TextOverflow>{props.value}</S.Span.TextOverflow>
            </S.DataGrid.CenterDataGridCell>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, typeOptions, responsibleOptions])

  const handleRowClick = (item: GridRowModel) => {
    const taskType = item?.row?.taskType
    if (taskType !== TASK.FINISH_JOURNAL) {
      return
    }

    const journalId = item?.row?.relatedEntityId
    const process = item?.row?.process

    if (process === PROJECT.KAP) {
      const programId = item?.row?.entityId
      navigateToKapProgramJournal(programId, journalId)
    } else {
      const projectId = item?.row?.entityId
      navigateToProjectJournal(process, projectId, journalId)
    }
  }

  const navigateToProjectJournal = (process: PROJECT_TYPE, projectId: string, journalId: string) => {
    switch (process) {
      case PROJECT.PF_KAP:
        navigate(ROUTES.PfKapProjectJournalEdit.params({ projectId: projectId, journalId: journalId }))
        break
      case PROJECT.PF_PGV:
        navigate(ROUTES.PfPgvProjectJournalEdit.params({ projectId: projectId, journalId: journalId }))
        break
      default:
        navigate('/')
    }
  }

  const navigateToKapProgramJournal = (programId: string, journalId: string) => {
    navigate(ROUTES.KapProgramJournalEdit.params({ programId: programId, journalId: journalId }))
  }

  return (
    <DataGridContainer>
      <DataGridProStyled
        rows={rows}
        columns={columns}
        loading={loading}
        apiRef={apiRef as any}
        localeText={gridTranslations}
        onRowClick={(props) => handleRowClick(props)}
        disableRowSelectionOnClick
        disableColumnReorder
        disableColumnSelector
        disableColumnPinning
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50]}
        pagination
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{
          toolbar: () => <GridToolbar title={getMessage('label.tasks.journal.title')} />,
        }}
      />
    </DataGridContainer>
  )
}
